import { uniformInt } from 'pure-rand/distribution/uniformInt';
import { xoroshiro128plus } from 'pure-rand/generator/xoroshiro128plus';
import type { RandomGenerator } from 'pure-rand/types/RandomGenerator';
import type { Rng } from './ports';

const UINT32_MAX = 0xffffffff;
const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/**
 * FNV-1a 32-bit hash for mixing string labels into RNG fork seeds.
 * Keeps fork IDs stable across runs — two runs forking with the same label
 * from the same parent state produce identical sub-RNGs.
 */
function fnv1a(label: string): number {
  let hash = FNV_OFFSET;
  for (let i = 0; i < label.length; i++) {
    hash ^= label.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME);
  }
  return hash | 0;
}

function normalizeSeed(seed: number | string): number {
  if (typeof seed === 'number') {
    if (!Number.isInteger(seed)) {
      throw new RangeError(`Rng seed must be an integer, got ${seed}`);
    }
    return seed | 0;
  }
  return fnv1a(seed);
}

class PureRandRng implements Rng {
  private state: RandomGenerator;
  readonly label: string;

  constructor(stateOrSeed: RandomGenerator | number, label: string) {
    this.state = typeof stateOrSeed === 'number' ? xoroshiro128plus(stateOrSeed) : stateOrSeed;
    this.label = label;
  }

  next(): number {
    return uniformInt(this.state, 0, UINT32_MAX) / (UINT32_MAX + 1);
  }

  int(n: number): number {
    if (!Number.isInteger(n) || n <= 0) {
      throw new RangeError(`Rng.int requires positive integer, got ${n}`);
    }
    return uniformInt(this.state, 0, n - 1);
  }

  shuffle<T>(items: ReadonlyArray<T>): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const tmp = out[i];
      out[i] = out[j];
      out[j] = tmp;
    }
    return out;
  }

  fork(label: string): Rng {
    const parentDraw = uniformInt(this.state, 0, UINT32_MAX);
    const childSeed = (parentDraw ^ fnv1a(label)) | 0;
    return new PureRandRng(childSeed, `${this.label}/${label}`);
  }
}

/**
 * Create a seedable deterministic RNG backed by pure-rand xoroshiro128plus.
 * Engine code must funnel all randomness through this interface — no Math.random.
 */
export function createRng(seed: number | string, label = 'root'): Rng {
  return new PureRandRng(normalizeSeed(seed), label);
}
